import { Link } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useState } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import LightModeIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeIcon from "@mui/icons-material/DarkModeOutlined";
import LanguageIcon from "@mui/icons-material/LanguageOutlined";
import { useTranslation } from "react-i18next";
import { assets } from "@/lib/assets";
import { useTheme } from "@/lib/theme";
import { btnPrimary, container } from "@/lib/ui";

const navItems = [
  { to: "/", key: "nav.home" },
  { to: "/products", key: "nav.products" },
  { to: "/about", key: "nav.about" },
  { to: "/contact", key: "nav.contact" },
] as const;

export function Header() {
  const { t, i18n } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const lang = i18n.resolvedLanguage === "en" ? "en" : "zh";
  const toggleLang = () => void i18n.changeLanguage(lang === "zh" ? "en" : "zh");
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  // Every page opens on an always-dark hero/banner. While the header is
  // transparent over it (not scrolled, menu closed) we must use light colors
  // so it stays legible in the light theme too.
  const glass = scrolled || menuOpen;
  const onDark = !glass;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        glass
          ? "border-b border-hairline bg-bg/70 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className={`${container} flex h-16 items-center justify-between md:h-20`}>
        <Link to="/" className="flex items-center gap-2.5" onClick={() => setMenuOpen(false)}>
          <img src={assets.logo} alt="赋维世" className="h-8 w-8 md:h-9 md:w-9" />
          <span className="flex flex-col leading-none">
            <span
              className={`font-display text-lg font-bold tracking-wide ${
                onDark ? "text-white" : "text-fg"
              }`}
            >
              赋维世
            </span>
            <span
              className={`text-[10px] font-medium uppercase tracking-[0.25em] ${
                onDark ? "text-white/55" : "text-fg-faint"
              }`}
            >
              Furtherverse
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`relative rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                onDark ? "text-white/75 hover:text-white" : "text-fg-muted hover:text-fg"
              }`}
              activeProps={{ className: onDark ? "!text-white" : "!text-fg" }}
              activeOptions={{ exact: item.to === "/" }}
            >
              {({ isActive }) => (
                <>
                  {t(item.key)}
                  {isActive && (
                    <motion.span
                      layoutId="nav-pill"
                      className={`absolute inset-0 -z-10 rounded-full ring-1 ${
                        onDark ? "bg-white/15 ring-white/25" : "bg-brand/10 ring-brand/30"
                      }`}
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                    />
                  )}
                </>
              )}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={toggleLang}
            aria-label={t("language.switch")}
            className={`hidden h-10 items-center justify-center rounded-full border px-3 text-sm font-semibold transition-colors md:flex ${
              onDark
                ? "border-white/25 text-white/85 hover:border-brand-cyan/60 hover:text-white"
                : "border-hairline text-fg-muted hover:border-brand/50 hover:text-fg"
            }`}
          >
            {lang === "zh" ? "EN" : "中"}
          </button>
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={t("theme.toggle")}
            className={`hidden h-10 w-10 items-center justify-center rounded-full border transition-colors md:flex ${
              onDark
                ? "border-white/25 text-white/85 hover:border-brand-cyan/60 hover:text-white"
                : "border-hairline text-fg-muted hover:border-brand/50 hover:text-fg"
            }`}
          >
            {theme === "dark" ? (
              <LightModeIcon style={{ fontSize: 20 }} />
            ) : (
              <DarkModeIcon style={{ fontSize: 20 }} />
            )}
          </button>

          <Link to="/contact" className={`${btnPrimary} hidden !py-2.5 !px-5 md:inline-flex`}>
            {t("actions.cooperate")}
          </Link>

          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="菜单"
            className={`flex h-10 w-10 items-center justify-center rounded-full border md:hidden ${
              onDark ? "border-white/25 text-white" : "border-hairline text-fg"
            }`}
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25 }}
            className="border-t border-hairline bg-bg/95 backdrop-blur-xl md:hidden"
          >
            <nav className={`${container} flex flex-col gap-1 py-4`}>
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                  className="rounded-xl px-4 py-3 text-base font-medium text-fg-muted transition-colors hover:bg-surface hover:text-fg"
                  activeProps={{ className: "!text-fg bg-surface" }}
                  activeOptions={{ exact: item.to === "/" }}
                >
                  {t(item.key)}
                </Link>
              ))}
              <button
                type="button"
                onClick={toggleTheme}
                className="flex items-center gap-3 rounded-xl px-4 py-3 text-base font-medium text-fg-muted transition-colors hover:bg-surface hover:text-fg"
              >
                {theme === "dark" ? (
                  <LightModeIcon style={{ fontSize: 20 }} />
                ) : (
                  <DarkModeIcon style={{ fontSize: 20 }} />
                )}
                {theme === "dark" ? t("theme.toLight") : t("theme.toDark")}
              </button>
              <button
                type="button"
                onClick={toggleLang}
                className="flex items-center gap-3 rounded-xl px-4 py-3 text-base font-medium text-fg-muted transition-colors hover:bg-surface hover:text-fg"
              >
                <LanguageIcon style={{ fontSize: 20 }} />
                {lang === "zh" ? "English" : "中文"}
              </button>
              <Link
                to="/contact"
                onClick={() => setMenuOpen(false)}
                className={`${btnPrimary} mt-2`}
              >
                {t("actions.cooperate")}
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
